import React, {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import Button from '../../../common/Button/Button'

import Slider from '../Slider/Slider'
import css from './ProductDescriptionMobile.module.scss'
import LeafIcon from '../../../../public/static/icons/catalog/ico-sheet.svg'
import DropIcon from '../../../../public/static/icons/catalog/ico-drop.svg'
import RabbitIcon from '../../../../public/static/icons/catalog/ico-rabbit.svg'
import CheckIcon from '../../../../public/static/icons/check-1.svg'
import AccordionText from '../../../common/AccordionText/AccordionText'
import {
  ButtonTheme,
  IBreadcrumb,
  IParamPick,
  IProductDetail,
  IProductSlide,
} from '../../../types'
import cx from 'classnames'
import { clearHtml, formatPrice } from '../../../../lib/utils'
import FavoriteBtn from '../FavoriteBtn/FavoriteBtn'
import SubSets from '../ProductDescription/SubSets'
import { CartContext } from '../../../../contexts/cart'
import Countdown from '../Countdown/Countdown'
import {
  product,
  productVariables,
  product_product_sku_propertyValuesRelation_targetElement,
} from '../../../../__generated__/product'
import { ApolloQueryResult } from '@apollo/client'
import {
  julirea_product,
  julirea_productVariables,
} from '../../../../__generated__/julirea_product'
import ParamPicker from '../ParamPicker/ParamPicker'
import ym from 'react-yandex-metrika'
import Rating from '../../../review/Rating'
import scrollToElement from 'scroll-to-element'
import Counter from '../Counter/Counter'
import useNotifications from '../../../../hooks/useNotifications'
import AddToCardNotification from '../../../common/notify/AddToCardNotification'
import { trackVKPurchaseGoal } from '../../../../lib/trackVKPurchaseGoal'

interface IProductDescriptionMobile {
  product: IProductDetail
  test: product_product_sku_propertyValuesRelation_targetElement
  pickedSku: IParamPick
  setPickedSku: Dispatch<SetStateAction<IParamPick>>
  pickedSkuAroma: IParamPick
  setPickedSkuAroma: Dispatch<SetStateAction<IParamPick>>
  hasSubsets: boolean
  subsetsIds: string[]
  discountActive: boolean
  pickedColors: Record<string, IParamPick>
  pickColor: (setId: string, sku: IParamPick) => void
  slides: IProductSlide[]
  discountActiveTo: number
  refetch: (
    variables?: Partial<productVariables | julirea_productVariables>,
  ) => Promise<ApolloQueryResult<product | julirea_product>>
  isJulirea?: boolean
  breadcrumbs: IBreadcrumb[]
}

const ProductDescriptionMobile: FC<IProductDescriptionMobile> = ({
  product,
  test,
  pickedSku,
  setPickedSku,
  pickedSkuAroma,
  setPickedSkuAroma,
  hasSubsets,
  subsetsIds,
  discountActive,
  pickColor,
  pickedColors,
  slides,
  breadcrumbs,
  discountActiveTo,
  refetch,
  isJulirea,
}) => {
  const [lastClicked, setLastClicked] = useState<number | null>(null)
  const { add, addSet, cart, loading, remove, setQuantity } =
    useContext(CartContext)
  const [itemInCart, setItemInCart] = useState(null)
  const [show, setShow] = useState(false)
  const cartRef = useRef(null)
  const [showNotification, setShowNotification] = useState(false)
  const { addNotification, notifications, removeNotification } =
    useNotifications<{
      image: string
      title: string
      variant?: {
        image: string
        title: string
      }
    }>()

  useEffect(() => {
    setItemInCart(
      cart?.items?.find(
        (item) => item.element.id === (pickedSku?.id || product?.id),
      ) || null,
    )
  }, [cart?.items, pickedSku])

  useEffect(() => {
    if (!(lastClicked !== null && !loading && showNotification)) return

    if (lastClicked === 0) {
      addNotification({
        image: slides[0].img,
        title: product.subtitle
          ? `${product.title} ${product.subtitle}`
          : product.title,
        variant: pickedSku?.item
          ? { image: pickedSku.image, title: pickedSku.item }
          : undefined,
      })
    } else if (lastClicked === 1) {
      addNotification({
        image: slides[0].img,
        title: test.name,
      })
    }

    setShowNotification(false)
  }, [loading])

  useEffect(() => {
    if (!cartRef?.current) return

    const callback = (entries) => {
      entries.forEach((entry) => {
        setShow(!entry.isIntersecting && entry.boundingClientRect.bottom < 0)
      })
    }

    const observer = new IntersectionObserver(callback)

    observer.observe(cartRef.current)

    return () => cartRef.current && observer.unobserve(cartRef.current)
  }, [cartRef])

  if (!product) {
    return null
  }

  function addToCart() {
    if (loading) {
      return
    }
    let name = product.subtitle
      ? `${product.title} ${product.subtitle}`
      : product.title
    let id = product.bx_id
    let price = product.price

    if (pickedSku) {
      name += ` ${pickedSku.artNumber && pickedSku.artNumber} ${
        pickedSku?.item
      }`
      id = pickedSku.bx_id
      price = pickedSku.price
    }
    setLastClicked(0)
    setShowNotification(true)
    ym('reachGoal', 'km_add_to_cart_from_detail_page')

    trackVKPurchaseGoal('add_to_cart')

    hasSubsets
      ? addSet(
          pickedSku?.id || product?.id,
          subsetsIds,
          [
            {
              name,
              id,
              price,
              quantity: 1,
              category: isJulirea
                ? breadcrumbs?.map((b) => b.name)?.join(' ')
                : breadcrumbs[breadcrumbs?.length - 1]?.name,
            },
          ],
          1,
        )
      : add(
          pickedSku?.id || product?.id,
          {
            name,
            id,
            price,
            quantity: 1,
            category: isJulirea
              ? breadcrumbs?.map((b) => b.name)?.join(' ')
              : breadcrumbs[breadcrumbs?.length - 1]?.name,
          },
          1,
        )
  }

  function renderButton() {
    return itemInCart && !(lastClicked === 0 && loading) ? (
      <>
        <div className={css.inCart}>
          <CheckIcon />В корзине
        </div>
        <Counter
          count={itemInCart.quantity}
          allowBelowOne
          setCount={(count) => {
            if (count <= 0) {
              remove(itemInCart.id)
              return
            }
            setQuantity(itemInCart.id, count)
          }}
        />
      </>
    ) : (
      <Button
        theme={ButtonTheme.black}
        className={css.cart}
        onClick={addToCart}
        loading={lastClicked === 0 && loading}
      >
        Добавить в корзину
        <span className={css.dot} />
        {pickedSku ? (
          <>
            <span>{formatPrice(pickedSku.price)}</span>
            {pickedSku.priceOld !== pickedSku.price && (
              <span className={css.oldPrice}>
                {formatPrice(pickedSku.priceOld)}
              </span>
            )}
          </>
        ) : (
          <>
            <span>{formatPrice(product.price)}</span>
            {product.oldPrice !== product.price && (
              <span className={css.oldPrice}>
                {formatPrice(product.oldPrice)}
              </span>
            )}
          </>
        )}
      </Button>
    )
  }

  return (
    <section className={css.wrapper}>
      <Slider slides={slides} />

      <div className={css.inner}>
        <div className={css.btnWrapper}>
          <FavoriteBtn className={css.favoriteBtn} id={product.id} />
        </div>

        {product.rate && !!product.reviewCount && (
          <Rating
            rating={product.rate}
            reviewsCount={product.reviewCount}
            className={css.rating}
            onClick={() => scrollToElement('#product-reviews')}
          />
        )}

        <p className={css.price}>
          {pickedSku ? (
            <>
              <span>{formatPrice(pickedSku.price)}</span>
              {pickedSku.priceOld !== pickedSku.price && (
                <span className={css.oldPrice}>
                  {formatPrice(pickedSku.priceOld)}
                </span>
              )}
            </>
          ) : (
            <>
              <span>{formatPrice(product.price)}</span>
              {product.oldPrice !== product.price && (
                <span className={css.oldPrice}>
                  {formatPrice(product.oldPrice)}
                </span>
              )}
            </>
          )}
        </p>

        <h1 className={cx('apoc', 'h2', css.title)}>{product.title}</h1>
        {product.subtitle && <p className={css.subtitle}>{product.subtitle}</p>}

        {product.external?.length > 0 && (
          <ul className={css.external__list}>
            {product?.external?.map((ext, index) => (
              <li
                className={css.external__item}
                key={`product_external_${index}`}
              >
                <span className={css.descr}>{ext.description}: </span>
                <span>{ext.value}</span>
              </li>
            ))}
          </ul>
        )}

        {product.colors?.length > 0 && (
          <ParamPicker
            params={product.colors}
            picked={pickedSku}
            onPick={setPickedSku}
            type="color"
          />
        )}

        {product.aromates?.length > 0 && (
          <ParamPicker
            params={product.aromates}
            picked={pickedSkuAroma}
            onPick={setPickedSkuAroma}
            type="aroma"
          />
        )}

        {product?.subsets?.length > 0 && (
          <SubSets
            items={product.subsets}
            pickColor={pickColor}
            pickedColors={pickedColors}
          />
        )}

        {discountActive && (
          <Countdown
            discountActive={discountActive}
            discountActiveTo={discountActiveTo}
            refetch={refetch}
          />
        )}
        <div ref={cartRef} className={css.cartContainer}>
          {renderButton()}
        </div>
        <div
          className={cx(
            css.cartContainer,
            css.cartFixed,
            show && css['cartFixed--show'],
          )}
        >
          {renderButton()}
        </div>

        {test && (
          <Button
            theme={ButtonTheme.white}
            className={css.test}
            loading={lastClicked === 1 && loading}
            onClick={() => {
              if (loading) {
                return
              }
              const name = test.name
              const id = test.bx_id
              const price = +test.priceBase
              setLastClicked(1)
              setShowNotification(true)
              ym('reachGoal', 'km_add_to_cart_probnik_from_detail_page')
              add(
                test.id,
                {
                  name,
                  id,
                  price,
                  quantity: 1,
                  category: isJulirea
                    ? breadcrumbs?.map((b) => b.name)?.join(' ')
                    : breadcrumbs[breadcrumbs?.length - 1]?.name,
                },
                1,
              )
            }}
          >
            Купить Пробник&nbsp;—&nbsp;<span>{test.priceBase} ₽</span>
          </Button>
        )}

        <div
          className="podeli_widget--mobile"
          data-total={pickedSku ? pickedSku.price : product.price}
        />

        {product.description && (
          <div
            className={css.description_text}
            dangerouslySetInnerHTML={{ __html: clearHtml(product.description) }}
          />
        )}

        {!isJulirea && (
          <ul className={css.garanty_wrapper}>
            <li className={css.garanty}>
              <LeafIcon viewBox="0 0 49 49" />

              <p className={css.garanty_description}>
                натуральные гипоаллергенные компоненты
              </p>
            </li>
            <li className={css.garanty}>
              <DropIcon viewBox="0 0 49 49" />

              <p className={css.garanty_description}>
                не&nbsp;содержит тальк, силиконы и&nbsp;отдушки
              </p>
            </li>
            <li className={css.garanty}>
              <RabbitIcon viewBox="0 0 49 49" />

              <p className={css.garanty_description}>
                не&nbsp;тестируется на&nbsp;животных
              </p>
            </li>
          </ul>
        )}

        {!!product.accordions?.length && (
          <div className={css.accordions}>
            {product.accordions.map((accordion) => (
              <AccordionText
                key={accordion.id}
                title={
                  <span className={css.accordions_title}>
                    {accordion.title}
                  </span>
                }
                titleUppercase
              >
                <div
                  className={cx('content', css.accordions_description)}
                  dangerouslySetInnerHTML={{
                    __html: clearHtml(accordion.description),
                  }}
                />
              </AccordionText>
            ))}
          </div>
        )}
      </div>
      {notifications.length > 0 &&
        notifications.map((notification) => (
          <AddToCardNotification
            key={notification.id}
            onDelete={() => removeNotification(notification.id)}
            image={notification.image}
            title={notification.title}
            variant={notification.variant}
          />
        ))}
    </section>
  )
}

export default ProductDescriptionMobile
